import {
  clearLlmPrivateBlock,
  fetchLlmPrivatePages,
  markLlmPrivateBlock,
} from '@/lib/blocks/api';
import { reportNetworkError } from '@/lib/net/net-status';
import { flushPendingSync } from '@/lib/sync/engine';

import {
  isLlmPrivateBlock,
  setLlmPrivateIds,
  setLlmPrivateMembership,
  subscribeLlmPrivate,
} from './llm-private-store';

export { isLlmPrivateBlock, subscribeLlmPrivate };

let privateLoadGeneration = 0;

function applyPrivateMembership(blockId: string, isPrivate: boolean): void {
  privateLoadGeneration += 1;
  setLlmPrivateMembership(blockId, isPrivate);
}

export async function loadLlmPrivate(): Promise<void> {
  const generation = ++privateLoadGeneration;
  try {
    const pages = await fetchLlmPrivatePages();
    if (generation !== privateLoadGeneration) {
      return;
    }
    setLlmPrivateIds(pages.map((page) => page.id));
  } catch {
    // Keep the last known flags. A failed refresh must not clear a toggle the user just made.
  }
}

export async function toggleLlmPrivate(blockId: string): Promise<boolean> {
  const next = !isLlmPrivateBlock(blockId);
  applyPrivateMembership(blockId, next);
  try {
    await flushPendingSync();
    if (next) {
      await markLlmPrivateBlock(blockId);
    } else {
      await clearLlmPrivateBlock(blockId);
    }
    await loadLlmPrivate();
    return isLlmPrivateBlock(blockId);
  } catch (err) {
    applyPrivateMembership(blockId, !next);
    reportNetworkError({
      message: next ? 'Could not mark this as private.' : 'Could not remove the private flag.',
      detail: err instanceof Error ? err.message : String(err),
    });
    return !next;
  }
}

export async function copyLlmPrivateFlag(sourceId: string, copyId: string): Promise<void> {
  await loadLlmPrivate();
  if (!isLlmPrivateBlock(sourceId)) {
    return;
  }
  await markLlmPrivateBlock(copyId);
  await loadLlmPrivate();
}
